<template>
  <div class="container pull-up">
    <div class="row">
      <div class="col-md-12">
        <div class="card m-b-30">
          <div class="card-header">
            <el-row>
              <el-col :span="12">
                <div class="m-b-0 h5">
                  <i class="mdi mdi-account-supervisor"></i> {{ $t('customer.button.all') }}
                </div>
              </el-col>
              <v-spacer/>
              <el-col :span="12">
                <el-input v-model="search" size="mini" autofocus
                          :placeholder="$t('text.search_placeholder')" ref="searchInput"/>
              </el-col>
            </el-row>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <data-tables
                  :data="customers.filter(data => !search || data.name.toLowerCase().includes(search.toLowerCase()))"
                  :pagination-props="{ pageSizes: [10, 15, 30, 40, 100], pageSize: 10 }"
                  center-align>
                <el-table-column label="Photo" width="80" align="center">
                  <template slot-scope="scope">
                    <div class="customer-avatar-container">
                      <img
                        v-if="scope.row.photo && scope.row.photo !== ''"
                        :src="scope.row.photo"
                        :alt="scope.row.name"
                        class="customer-avatar"
                        @error="handleImageError($event)"
                      />
                      <div v-else class="customer-avatar-placeholder">
                        <i class="mdi mdi-account-circle"></i>
                      </div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="name" :label="$t('customer.label.name')" min-width="120px">
                  <template #default="{ row }">
                    {{ row.name }} <v-chip color="info" v-if="hasNationality && row.nationality" dark x-small>{{ row.nationality?.code }}</v-chip>
                    <v-chip v-if="hasStore && row.store" x-small>{{ row.store?.name }}</v-chip>
                  </template>
                </el-table-column>
                <el-table-column prop="code" :label="$t('customer.label.code')">
                  <template slot-scope="scope">
                    <router-link :to="{ name: 'customer-details', params: { id: scope.row.id } }">
                      {{ scope.row.code }}
                    </router-link>
                  </template>
                </el-table-column>
                <el-table-column label="PL">
                  <template slot-scope="scope">
                    <el-select :clearable="$can('customer.price-list')" :value="scope.row.price_list_id" size="mini" @change="updatePriceList(scope.row.id, $event)">
                      <el-option v-can.disable="'customer.price-list'" v-for="pl in price_lists" :key="pl.id" :label="pl.name" :value="pl.id"></el-option>
                    </el-select>
                  </template>
                </el-table-column>
                <el-table-column show-overflow-tooltip prop="address" :label="$t('customer.label.address')"></el-table-column>
                <el-table-column align="center" :label="$t('customer.label.buy_on_credit')">
                  <template slot-scope="scope">
                    <el-switch @change="updateCreditCustomer(scope.row.id)" :value="scope.row.buy_on_credit"></el-switch>
                  </template>
                </el-table-column>
                <el-table-column prop="phone" :label="$t('customer.label.phone')" :formatter="$formatPhone"></el-table-column>
                <el-table-column prop="created_at" :formatter="$slimDate" :label="$t('label.date')"></el-table-column>
                <el-table-column show-overflow-tooltip prop="user.name" :label="$t('customer.label.user')">
                </el-table-column>
                <el-table-column :label="$t('title.actions')" fixed="right">
                  <template slot-scope="scope">
                    <v-btn x-small color="primary" v-can.disable="'customer.update'" @click.prevent="editCustomer(scope.row)" class="mr-2">
                      <v-icon left x-small>mdi-pencil</v-icon>{{ $t('button.edit') }}
                    </v-btn>
                    <el-dropdown trigger="click">
                      <span class="el-dropdown-link">
                        <i class="el-icon-more"></i>
                      </span>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item v-can.disable="'customer.archive'" @click.native.prevent="archiveCustomer(scope.row.id)">
                          <i class="mdi mdi-archive"></i> {{ $t('button.archive') }}
                        </el-dropdown-item>
                        <el-dropdown-item class="text-danger" v-can.disable="'customer.delete'" @click.native.prevent="deleteCustomer(scope.row.id)">
                          <i class="mdi mdi-delete"></i> {{ $t('button.delete') }}
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </el-table-column>
              </data-tables>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'Index',
  data () {
    return {
      price_lists: [],
      customers: [],
      customer: {},
      detailsDialog: false,
      search: ''
    }
  },
  computed: {
    hasStore () {
      return this.customers.length > 0 && Object.keys(this.customers[0]).indexOf('store_id') > -1
    },
    hasNationality () {
      return this.customers.length > 0 && Object.keys(this.customers[0]).indexOf('country_id') > -1
    }
  },
  methods: {
    handleImageError (event) {
      // Hide the broken image and show placeholder
      event.target.style.display = 'none'
      const placeholder = event.target.nextElementSibling
      if (placeholder) {
        placeholder.style.display = 'flex'
      }
    },
    editCustomer (customer) {
      this.$router.push({
        name: 'edit-customer',
        params: { id: customer.id }
      })
    },
    async updatePriceList (customerId, plId) {
      try {
        await this.withLoading(async () => {
          const res = await this.$customerService.updatePriceList(customerId, plId)
          this.customers.find(c => c.id === customerId).price_list_id = plId
          this.showSuccess(this.$t('customer.message.price_list_updated'))
        }, 'loading')
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error in updatePriceList:', error)
      }
    },
    async archiveCustomer (id) {
      this.askQuestion(this.$t('customer.confirm.archive'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$customerService.archiveCustomer(id)
            this.showSuccess(this.$t('customer.message.archived'))
            this.customers.splice(this.customers.indexOf(this.customers.find(c => c.id === id)), 1)
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          console.log('Component caught error in archiveCustomer:', error)
        }
      })
    },
    async updateCreditCustomer (id) {
      try {
        await this.withLoading(async () => {
          const data = await this.$customerService.updateCreditCustomer(id)
          this.customers.find(c => c.id === id).buy_on_credit = data.buy_on_credit
        }, 'loading')
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error in updateCreditCustomer:', error)
      }
    },
    async deleteCustomer (id) {
      this.askQuestion(this.$t('customer.confirm.delete'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$customerService.deleteCustomer(id)
            this.showSuccess(this.$t('customer.message.deleted'))
            this.customers.splice(this.customers.indexOf(this.customers.find(c => c.id === id)), 1)
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          console.log('Component caught error in deleteCustomer:', error)
        }
      })
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('customer.title.customer'))
    setTimeout(() => {
      this.$refs.searchInput.focus()
    }, 500)

    try {
      await this.withLoading(async () => {
        this.price_lists = await this.$configService.getPriceLists()
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getPriceLists:', error)
    }

    try {
      await this.withLoading(async () => {
        this.customers = await this.$customerService.getCustomers()
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getCustomers:', error)
    }
  }
}
</script>

<style scoped>
.customer-avatar-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.customer-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
}

.customer-avatar-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #f5f5f5;
  border: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 28px;
}
</style>
